import React, { useEffect, useState, useRef } from 'react';
import { Spin, Table, Modal, Button } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUnapprovedLoansAction,
  updateUnapprovedLoanAction,
} from '../../Loan/store/loanActions';
import { type Customer, type Loan } from '../../Customer/store/customerTypes';
import LoanModal from '../../Customer/LoanModal';
import ApprovalCustomerDetails from '../ApprovalCustomerDetails';

export default function LoanApprovalsIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const { unapprovedLoans }: any = useSelector<any>((state) => state?.loan);

  const getLoans = async (): Promise<void> => {
    await getUnapprovedLoansAction(dispatch, {});
    processingRef.current = false;
  };

  const [selectedLoan, setSelectedLoan] = useState<Loan>();
  const [selectedCustomer, setSelectedCustomer] = useState<Customer>();

  useEffect(() => {
    void getLoans();
  }, []);

  const processingRef = useRef(false);

  const handleUpdate = async (loan: Loan, status: string) => {
    if (processingRef.current) return;
    processingRef.current = true;
    await updateUnapprovedLoanAction(dispatch, { id: loan._id, status });
    void getLoans();
    processingRef.current = false;
  };

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: Loan, index: number) => index + 1,
      width: 50,
    },
    {
      title: 'Loan Number',
      dataIndex: 'loanNumber',
      key: 'loanNumber',
      render: (_: any, record: Loan, index: number) => (
        <p>{record.loanNumber}</p>
      ),
    },
    {
      title: 'Customer',
      dataIndex: 'customer',
      key: 'customer',
      render: (_: any, record: Loan, index: number) => (
        <img src={record.customer?.photo} style={{ height: 130, width: 130 }} />
      ),
    },
    {
      title: 'Customer Details',
      dataIndex: 'customerDetails',
      key: 'customerDetails',
      render: (_: any, record: Loan) => (
        <ApprovalCustomerDetails customer={record.customer} />
      ),
    },
    {
      title: 'Principal',
      dataIndex: 'principal',
      key: 'principal',
      width: 40,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.principal}</p>
      ),
    },
    {
      title: 'Approval Type',
      dataIndex: 'approvalType',
      key: 'approvalType',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.approvalType}</p>
      ),
    },
    {
      title: 'Loan Average',
      dataIndex: 'loanAverage',
      key: 'loanAverage',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.loanAverage?.toFixed(0)}</p>
      ),
    },
    {
      title: 'Total Gross Weight',
      dataIndex: 'grossWeight',
      key: 'grossWeight',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>
          {record.jewels
            ?.reduce(
              (total: number, jewel: any) => total + jewel.grossWeight,
              0
            )
            ?.toFixed(2) ?? 0}
        </p>
      ),
    },
    {
      title: 'Total Net Weight',
      dataIndex: 'netWeight',
      key: 'netWeight',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>
          {record.jewels
            ?.reduce((total: number, jewel: any) => total + jewel.netWeight, 0)
            ?.toFixed(2) ?? 0}
        </p>
      ),
    },
    {
      title: 'Calculated Principal',
      dataIndex: 'calculatedPrincipal',
      key: 'calculatedPrincipal',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.calculatedPrincipal}</p>
      ),
    },
    {
      title: 'Requested Principal',
      dataIndex: 'requestedPrincipal',
      key: 'requestedPrincipal',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.principal}</p>
      ),
    },
    {
      title: 'Calculated Document Charge',
      dataIndex: 'calculatedDocumentCharge',
      key: 'calculatedDocumentCharge',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.calculatedDocumentCharges}</p>
      ),
    },
    {
      title: 'Requested Document Charge',
      dataIndex: 'requestedDocumentCharge',
      key: 'requestedDocumentCharge',
      width: 60,
      render: (_: any, record: Loan, index: number) => (
        <p>{record.documentCharge}</p>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'actions',
      width: 100,
      render: (_: any, record: Loan) => (
        <p
          style={{
            color: '#DD1E2D',
            textDecoration: 'underline',
            cursor: 'pointer',
          }}
          onClick={async () => {
            setSelectedLoan(record);
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
            setSelectedCustomer(record.customer);
            showModal();
          }}
        >
          View Details
        </p>
      ),
    },
    {
      title: 'Approve',
      dataIndex: 'actions',
      width: 100,
      render: (_: any, record: Loan, index: number) => (
        <Button
          type="primary"
          style={{
            backgroundColor: '#387F39', // Custom background color
            borderColor: '#387F39', // Custom border color to match the background
          }}
          onClick={() => {
            if (!processingRef.current) {
              void handleUpdate(record, 'approve');
            }
          }}
        >
          Approve
        </Button>
      ),
    },
    {
      title: 'Reject',
      dataIndex: 'actions2',
      width: 100,
      render: (_: any, record: Loan, index: number) => (
        <Button
          type="primary"
          style={{
            backgroundColor: '#DD1E2D', // Custom background color
            borderColor: '#DD1E2D', // Custom border color to match the background
          }}
          onClick={() => {
            if (!processingRef.current) {
              void handleUpdate(record, 'reject');
            }
          }}
        >
          Reject
        </Button>
      ),
    },
  ];

  const [isModalOpen, setIsModalOpen] = useState(false);
  const showModal = () => {
    setIsModalOpen(true);
  };
  const handleClose = () => {
    setIsModalOpen(false);
  };

  return (
    <div className="users__table__page">
      {!(unapprovedLoans.length > 0) && <Spin spinning={false} fullscreen />}
      <div style={{ width: '100%' }}>
        <Table dataSource={unapprovedLoans} columns={columns} />
      </div>
      <Modal
        width={1129}
        title="Loan Details"
        open={isModalOpen}
        onCancel={handleClose}
        footer={() => <></>}
      >
        <LoanModal
          openPrintModal={() => {}}
          customer={selectedCustomer}
          transactions={[]}
          loanDetail={selectedLoan}
        />
      </Modal>
    </div>
  );
}
